import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getOptionalObject,
  getBoolean,
  getOptionalLeoUuid,
  getList,
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "./code";
import { QuestionType } from "./questionType";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { Order, OrderDTO } from "./order";

export interface SurveyQuestionDetailsDTO {
  questionId: string;
  questionCode: CodeDTO;
  questionType: QuestionType.QuestionTypeDTO;
  question?: SurveyLocalizedTextDTO | null;
  order: OrderDTO;
  isQuestionVisible: boolean;
  isRuleApplied: boolean;
  parentQuestionId?: string | null;
  childQuestionIds: string[];
  isMandatory: boolean;
}

export class SurveyQuestionDetails {
  readonly isSurveyQuestionDetailsSurveyQuestionDetails: boolean = true;

  readonly questionId: LeoUUID;
  readonly questionCode: Code;
  readonly questionType: QuestionType.QuestionType;
  readonly question: SurveyLocalizedText | null;
  readonly order: Order;
  readonly isQuestionVisible: boolean;
  readonly isRuleApplied: boolean;
  readonly parentQuestionId: LeoUUID | null;
  readonly childQuestionIds: LeoUUID[];
  readonly isMandatory: boolean;


  constructor(
  questionId: LeoUUID, 
  questionCode: Code, 
  questionType: QuestionType.QuestionType, 
  question: SurveyLocalizedText | null = null, 
  order: Order, 
  isQuestionVisible: boolean, 
  isRuleApplied: boolean, 
  parentQuestionId: LeoUUID | null = null, 
  childQuestionIds: LeoUUID[], 
  isMandatory: boolean
  ) {
    this.questionId = questionId;
    this.questionCode = questionCode;
    this.questionType = questionType;
    this.question = question;
    this.order = order;
    this.isQuestionVisible = isQuestionVisible;
    this.isRuleApplied = isRuleApplied;
    this.parentQuestionId = parentQuestionId;
    this.childQuestionIds = childQuestionIds;
    this.isMandatory = isMandatory;
  }

  static fromDTO(dto: SurveyQuestionDetailsDTO): SurveyQuestionDetails {
    const questionId = getLeoUuid(dto, "questionId", InvalidSurveyQuestionDetailsQuestionIdError);
    const questionCode = Code.fromDTO(getObject(dto, "questionCode", InvalidSurveyQuestionDetailsQuestionCodeError));
    const questionType = QuestionType.fromDTO(getObject(dto, "questionType", InvalidSurveyQuestionDetailsQuestionTypeError));
    let question = getOptionalObject(dto, "question");
    if(question !== undefined && question !== null) {
      question = SurveyLocalizedText.fromDTO(getObject(dto, "question", InvalidSurveyQuestionDetailsQuestionError));
    }
    const order = Order.fromDTO(getObject(dto, "order", InvalidSurveyQuestionDetailsOrderError));
    const isQuestionVisible = getBoolean(dto, "isQuestionVisible", InvalidSurveyQuestionDetailsIsQuestionVisibleError);
    const isRuleApplied = getBoolean(dto, "isRuleApplied", InvalidSurveyQuestionDetailsIsRuleAppliedError);
    const parentQuestionId = getOptionalLeoUuid(dto, "parentQuestionId", InvalidSurveyQuestionDetailsParentQuestionIdError);
    const childQuestionIds = getList<string, LeoUUID>(
      dto,
      "childQuestionIds",
      (e: string) => new LeoUUID(e),
      InvalidSurveyQuestionDetailsChildQuestionIdsError
    );
    const isMandatory = getBoolean(dto, "isMandatory", InvalidSurveyQuestionDetailsIsMandatoryError);
    return new SurveyQuestionDetails(
      questionId,
      questionCode,
      questionType,
      question,
      order,
      isQuestionVisible,
      isRuleApplied,
      parentQuestionId,
      childQuestionIds,
      isMandatory
    );
  }

  toDTO(): SurveyQuestionDetailsDTO {
    const dto: SurveyQuestionDetailsDTO = {
        questionId: this.questionId.toDTO(),
        questionCode: this.questionCode.toDTO(),
        questionType: QuestionType.toDTO(this.questionType),
        order: this.order.toDTO(),
        isQuestionVisible: this.isQuestionVisible,
        isRuleApplied: this.isRuleApplied,
        childQuestionIds: this.childQuestionIds.map((e: LeoUUID) => { return e.toDTO(); }),
        isMandatory: this.isMandatory
    };
    if (this.question !== undefined && this.question !== null){
      dto.question = this.question.toDTO();
    }
    if (this.parentQuestionId !== undefined && this.parentQuestionId !== null){
      dto.parentQuestionId = this.parentQuestionId.toDTO();
    }
    return dto;
  }
  }

SurveyQuestionDetails.prototype.toString = function toString(): string {
  return `SurveyQuestionDetails(questionId='${this.questionId}',questionCode='${this.questionCode}',questionType='${this.questionType}',question='${this.question}',order='${this.order}',isQuestionVisible='${this.isQuestionVisible}',isRuleApplied='${this.isRuleApplied}',parentQuestionId='${this.parentQuestionId}',childQuestionIds='${this.childQuestionIds}',isMandatory='${this.isMandatory}')`;
};

export class InvalidSurveyQuestionDetailsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsError";
  }
}
export class InvalidSurveyQuestionDetailsQuestionIdError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsQuestionIdError";
  }
}

export class InvalidSurveyQuestionDetailsQuestionCodeError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsQuestionCodeError";
  }
}

export class InvalidSurveyQuestionDetailsQuestionTypeError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsQuestionTypeError";
  }
}

export class InvalidSurveyQuestionDetailsQuestionError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsQuestionError";
  }
}

export class InvalidSurveyQuestionDetailsOrderError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsOrderError";
  }
}

export class InvalidSurveyQuestionDetailsIsQuestionVisibleError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsIsQuestionVisibleError";
  }
}

export class InvalidSurveyQuestionDetailsIsRuleAppliedError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsIsRuleAppliedError";
  }
}

export class InvalidSurveyQuestionDetailsParentQuestionIdError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsParentQuestionIdError";
  }
}

export class InvalidSurveyQuestionDetailsChildQuestionIdsError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsChildQuestionIdsError";
  }
}

export class InvalidSurveyQuestionDetailsIsMandatoryError extends InvalidSurveyQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyQuestionDetailsIsMandatoryError";
  }
}
